import { useState } from "react";
import { Box, Grid, IconButton, SxProps, Tooltip, Typography } from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useTranslation } from "react-i18next";
import { InfoCard, SnackBar } from "src/design";
import {
  BANK_TRANSFER_DETAILS,
  buildTransferReference,
} from "src/business/payment/bankDetails";
import {
  colorAccent,
  colorTextMuted,
  colorTextPrimary,
} from "src/design/tokens";

interface BankTransferCardProps {
  restaurantName: string;
  restaurantId: string;
  sx?: SxProps;
}

const Field = ({
  label,
  value,
  hint,
  action,
}: {
  label: string;
  value: string;
  hint?: string;
  action?: React.ReactNode;
}) => (
  <Grid item xs={12} md={6}>
    <Typography variant="caption" sx={{ color: colorTextMuted }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography
        sx={{ fontWeight: 600, color: colorTextPrimary, wordBreak: "break-all" }}
      >
        {value}
      </Typography>
      {action}
    </Box>
    {hint && (
      <Typography variant="caption" sx={{ color: colorTextMuted }}>
        {hint}
      </Typography>
    )}
  </Grid>
);

/**
 * Where to send the transfer. The backend exposes no bank-details endpoint by
 * design, so these come from a client constant; the reference is derived here
 * and never sent anywhere — it exists so ops can match a line on the bank
 * statement back to a restaurant.
 */
export const BankTransferCard = ({
  restaurantName,
  restaurantId,
  sx,
}: BankTransferCardProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const reference = buildTransferReference(restaurantName, restaurantId);

  // Retyping a RIB into a banking app is where a transfer goes to the wrong
  // account, so it is one action away from the clipboard.
  const copyRib = async () => {
    await navigator.clipboard.writeText(BANK_TRANSFER_DETAILS.rib);
    setCopied(true);
  };

  return (
    <>
      <InfoCard
        title={t("payments.bank.title")}
        subtitle={t("payments.bank.subtitle")}
        icon={<AccountBalanceOutlinedIcon sx={{ color: colorAccent }} />}
        sx={sx}
      >
        <Grid container spacing={2} sx={{ py: 1 }}>
          <Field
            label={t("payments.bank.accountHolder")}
            value={BANK_TRANSFER_DETAILS.accountHolder}
          />
          <Field
            label={t("payments.bank.rib")}
            value={BANK_TRANSFER_DETAILS.rib}
            action={
              <Tooltip title={t("payments.bank.copy")}>
                <IconButton size="small" onClick={copyRib}>
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
          <Field
            label={t("payments.bank.swift")}
            value={BANK_TRANSFER_DETAILS.swift}
          />
          <Field
            label={t("payments.bank.iban")}
            value={BANK_TRANSFER_DETAILS.iban}
            hint={t("payments.bank.ibanHint")}
          />
          <Field
            label={t("payments.bank.reference")}
            value={reference}
            hint={t("payments.bank.referenceHint")}
          />
        </Grid>
      </InfoCard>

      <SnackBar
        open={copied}
        message={t("payments.bank.copied")}
        severity="success"
        onClose={() => setCopied(false)}
      />
    </>
  );
};
